import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  FiUsers, FiPlus, FiEdit, FiTrash2, FiPhone, FiMail, FiRefreshCw,
  FiSearch, FiUser, FiCalendar, FiBriefcase, FiCheck, FiKey, FiEye,
  FiLock, FiUnlock, FiUserCheck, FiClock
} from 'react-icons/fi';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  'Technicien',
  'Animateur',
  'Commercial',
  'Photographe',
  'Assistant',
  'Manager'
];

const ROLE_COLORS = {
  'Technicien': 'bg-blue-100 text-blue-700',
  'Animateur': 'bg-purple-100 text-purple-700',
  'Commercial': 'bg-green-100 text-green-700',
  'Photographe': 'bg-amber-100 text-amber-700',
  'Assistant': 'bg-slate-100 text-slate-700',
  'Manager': 'bg-indigo-100 text-indigo-700'
};

export default function TeamPage() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [prestationsDialogOpen, setPrestationsDialogOpen] = useState(false);
  const [accessPassword, setAccessPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'Technicien'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load team members
      const teamRes = await fetch(`${API_URL}/api/team-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (teamRes.ok) {
        const data = await teamRes.json();
        setTeamMembers(data);
      }

      // Load reservations for assigned prestations
      const resRes = await fetch(`${API_URL}/api/reservations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resRes.ok) {
        const data = await resRes.json();
        setReservations(data.reservations || data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name || '',
        email: member.email || '',
        phone: member.phone || '',
        role: member.role || 'Technicien'
      });
    } else {
      setEditingMember(null);
      setFormData({ name: '', email: '', phone: '', role: 'Technicien' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      const url = editingMember 
        ? `${API_URL}/api/team-members/${editingMember.id}`
        : `${API_URL}/api/team-members`;
      
      const response = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingMember ? 'Membre mis à jour' : 'Membre ajouté');
        setDialogOpen(false);
        loadData();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Supprimer ce membre de l\'équipe ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/team-members/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Membre supprimé');
        loadData();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle access for a team member
  const handleToggleAccess = (member) => {
    setSelectedMember(member);
    setAccessPassword('');
    setAccessDialogOpen(true);
  };

  const handleGrantAccess = async () => {
    if (!selectedMember?.email) {
      toast.error('L\'email est requis pour créer un accès');
      return;
    }
    if (!accessPassword || accessPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/team-members/${selectedMember.id}/grant-access`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: accessPassword })
      });

      if (response.ok) {
        toast.success('Accès collaborateur créé');
        setAccessDialogOpen(false);
        loadData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de la création de l\'accès');
      }
    } catch (error) {
      toast.error('Erreur lors de la création de l\'accès');
    }
  };

  const handleRevokeAccess = async () => {
    try {
      const response = await fetch(`${API_URL}/api/team-members/${selectedMember.id}/revoke-access`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Accès révoqué');
        setAccessDialogOpen(false);
        loadData();
      } else {
        toast.error('Erreur lors de la révocation');
      }
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  // View assigned prestations for a member
  const handleViewPrestations = (member) => {
    setSelectedMember(member);
    setPrestationsDialogOpen(true);
  };

  // Get prestations assigned to a member
  const getMemberPrestations = (memberId) => {
    return reservations.filter(r => 
      r.assigned_team_members?.includes(memberId) ||
      r.assigned_members?.some(m => m.id === memberId || m === memberId)
    );
  };

  const filteredMembers = teamMembers.filter(member =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const totalMembers = teamMembers.length;
  const membersWithAccess = teamMembers.filter(m => m.has_access).length;
  const rolesCount = teamMembers.reduce((acc, m) => {
    acc[m.role] = (acc[m.role] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Équipe</h1>
          <p className="text-slate-500">Gérez les membres de votre équipe et leurs accès</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
          <FiPlus className="mr-2 h-4 w-4" />
          Ajouter un membre
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FiUsers className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalMembers}</p>
                <p className="text-sm text-slate-500">Membres</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiUserCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{membersWithAccess}</p>
                <p className="text-sm text-slate-500">Avec accès</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(rolesCount).slice(0, 2).map(([role, count]) => (
          <Card key={role}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${ROLE_COLORS[role]?.split(' ')[0] || 'bg-slate-100'}`}>
                  <FiBriefcase className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-sm text-slate-500">{role}s</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Rechercher un membre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={loadData}>
          <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Team Members List */}
      <Card>
        <CardHeader>
          <CardTitle>Membres de l'équipe</CardTitle>
          <CardDescription>
            Cliquez sur "Accès" pour permettre à un membre de se connecter et voir ses prestations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FiRefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-12">
              <FiUsers className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun membre trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {filteredMembers.map((member) => {
                  const prestations = getMemberPrestations(member.id);
                  const upcomingPrestations = prestations.filter(p => 
                    new Date(p.event_date || p.date) >= new Date()
                  );
                  
                  return (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${
                          member.has_access ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {member.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{member.name}</h3>
                            <Badge className={ROLE_COLORS[member.role] || 'bg-slate-100 text-slate-700'}>
                              {member.role}
                            </Badge>
                            {member.has_access && (
                              <Badge className="bg-green-100 text-green-700">
                                <FiKey className="mr-1 h-3 w-3" />
                                Accès actif
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                            {member.email && (
                              <span className="flex items-center gap-1">
                                <FiMail className="h-3 w-3" />
                                {member.email}
                              </span>
                            )}
                            {member.phone && (
                              <span className="flex items-center gap-1">
                                <FiPhone className="h-3 w-3" />
                                {member.phone}
                              </span>
                            )}
                            {upcomingPrestations.length > 0 && (
                              <span className="flex items-center gap-1 text-indigo-600">
                                <FiCalendar className="h-3 w-3" />
                                {upcomingPrestations.length} prestation(s) à venir
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* View Prestations */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewPrestations(member)}
                          className="text-indigo-600"
                        >
                          <FiEye className="h-4 w-4 mr-1" />
                          Prestations
                        </Button>
                        
                        {/* Toggle Access */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleToggleAccess(member)}
                          className={member.has_access ? 'text-green-600' : 'text-slate-600'}
                        >
                          {member.has_access ? (
                            <>
                              <FiUnlock className="h-4 w-4 mr-1" />
                              Accès
                            </>
                          ) : (
                            <>
                              <FiLock className="h-4 w-4 mr-1" />
                              Accès
                            </>
                          )}
                        </Button>
                        
                        {/* Edit */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDialog(member)}
                        >
                          <FiEdit className="h-4 w-4" />
                        </Button>
                        
                        {/* Delete */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(member.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Member Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingMember ? 'Modifier le membre' : 'Ajouter un membre'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nom du membre"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemple.com"
              />
              <p className="text-xs text-slate-500 mt-1">
                Requis pour donner un accès collaborateur
              </p>
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="06 12 34 56 78"
              />
            </div>
            <div>
              <Label>Rôle</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700">
              <FiCheck className="mr-2 h-4 w-4" />
              {editingMember ? 'Mettre à jour' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Management Dialog */}
      <Dialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiKey className="h-5 w-5 text-indigo-600" />
              Gestion de l'accès - {selectedMember?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedMember?.has_access 
                ? 'Ce membre a un accès actif. Il peut se connecter et voir ses prestations assignées.'
                : 'Créez un accès pour permettre à ce membre de se connecter et voir ses prestations.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {selectedMember?.has_access ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <FiCheck className="h-5 w-5" />
                  <span className="font-medium">Accès actif</span>
                </div>
                <p className="text-sm text-green-600">
                  Email de connexion : <strong>{selectedMember?.email}</strong>
                </p>
              </div>
            ) : (
              <>
                {!selectedMember?.email ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-700 text-sm">
                      Ce membre n'a pas d'email. Ajoutez d'abord un email pour créer un accès.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600">
                        Email : <strong>{selectedMember?.email}</strong>
                      </p>
                    </div>
                    <div>
                      <Label>Mot de passe</Label>
                      <Input
                        type="password"
                        value={accessPassword}
                        onChange={(e) => setAccessPassword(e.target.value)}
                        placeholder="Minimum 6 caractères"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessDialogOpen(false)}>
              Fermer
            </Button>
            {selectedMember?.has_access ? (
              <Button 
                onClick={handleRevokeAccess}
                className="bg-red-600 hover:bg-red-700"
              >
                <FiLock className="mr-2 h-4 w-4" />
                Révoquer l'accès
              </Button>
            ) : selectedMember?.email && (
              <Button 
                onClick={handleGrantAccess}
                className="bg-green-600 hover:bg-green-700"
              >
                <FiUnlock className="mr-2 h-4 w-4" />
                Créer l'accès
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prestations Dialog */}
      <Dialog open={prestationsDialogOpen} onOpenChange={setPrestationsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiCalendar className="h-5 w-5 text-indigo-600" />
              Prestations de {selectedMember?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 max-h-96 overflow-y-auto">
            {(() => {
              const memberPrestations = selectedMember ? getMemberPrestations(selectedMember.id) : [];
              
              if (memberPrestations.length === 0) {
                return (
                  <div className="text-center py-8">
                    <FiCalendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune prestation assignée</p>
                  </div>
                );
              }

              // Sort by date
              const sorted = [...memberPrestations].sort((a, b) => 
                new Date(b.event_date || b.date) - new Date(a.event_date || a.date)
              );

              return (
                <div className="space-y-3">
                  {sorted.map((prestation, idx) => {
                    const eventDate = new Date(prestation.event_date || prestation.date);
                    const isPast = eventDate < new Date();
                    
                    return (
                      <div 
                        key={prestation.id || idx}
                        className={`p-4 rounded-lg border ${
                          isPast ? 'bg-slate-50 border-slate-200' : 'bg-indigo-50 border-indigo-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-slate-900">
                                {prestation.client_name || prestation.client?.name || 'Client'}
                              </h4>
                              <Badge className={isPast ? 'bg-slate-200 text-slate-600' : 'bg-indigo-100 text-indigo-700'}>
                                {isPast ? 'Passée' : 'À venir'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              {prestation.event_type || prestation.formula_name || 'Prestation'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-slate-900">
                              {eventDate.toLocaleDateString('fr-FR', { 
                                day: 'numeric', 
                                month: 'short',
                                year: 'numeric'
                              })}
                            </p>
                            <p className="text-sm text-slate-500 flex items-center justify-end gap-1">
                              <FiClock className="h-3 w-3" />
                              {prestation.start_time || '14:00'}
                            </p>
                          </div>
                        </div>
                        {prestation.location && (
                          <p className="text-sm text-slate-500 mt-2">
                            📍 {prestation.location}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPrestationsDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
